import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import '../style/ageWiseSearch.css';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AgeWiseSearch = () => {
    // --- State ---
    const [showModal, setShowModal] = useState(true);
    const [loading, setLoading] = useState(false);
    const [voters, setVoters] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [cardSearchTerm, setCardSearchTerm] = useState("");

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const [formData, setFormData] = useState({
        from_age: '',
        to_age: ''
    });

    // 1. Pull language from context
    const { appId, language, userId } = useAppId();
    const navigate = useNavigate();

    // 2. Set default language to English
    const lang = language || 'en';

    // 3. Translation Dictionary
    const t = {
        en: {
            modalTitle: "Age Wise Search",
            modalSubtitle: "Enter age range to filter voters",
            fromAgeLabel: "From Age",
            toAgeLabel: "To Age",
            fromPlaceholder: "e.g. 18",
            toPlaceholder: "e.g. 23",
            cancelBtn: "Cancel",
            searchBtn: "Search Records",
            pageTitle: "Age Wise Search Results",
            searchAgainBtn: "Search Again",
            filterPlaceholder: "Filter displayed results by name...",
            loadingText: "Loading Data...",
            noRecordsText: "No records found.",
            fatherHusband: "F/S :",
            address: "Address :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            check: "Check",
            prachar: "Prachar",
            print: "Print",
            family: "Family",
            addToGroup: "Add to Group" // Added translation
        },
        hi: {
            modalTitle: "आयु अनुसार खोज",
            modalSubtitle: "मतदाताओं को फ़िल्टर करने के लिए आयु सीमा दर्ज करें",
            fromAgeLabel: "आयु (से)",
            toAgeLabel: "आयु (तक)",
            fromPlaceholder: "जैसे 18",
            toPlaceholder: "जैसे 23",
            cancelBtn: "रद्द करें",
            searchBtn: "रिकॉर्ड खोजें",
            pageTitle: "आयु अनुसार खोज परिणाम",
            searchAgainBtn: "फिर से खोजें",
            filterPlaceholder: "नाम से प्रदर्शित परिणाम फ़िल्टर करें...",
            loadingText: "डेटा लोड हो रहा है...",
            noRecordsText: "कोई रिकॉर्ड नहीं मिला।",
            fatherHusband: "पिता/पति :",
            address: "पता :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            check: "चेक",
            prachar: "प्रचार",
            print: "प्रिंट",
            family: "परिवार",
            addToGroup: "समूह में जोड़ें" // Added translation
        },
        gu: {
            modalTitle: "ઉંમર મુજબ શોધ",
            modalSubtitle: "મતદારોને ફિલ્ટર કરવા માટે ઉંમર શ્રેણી દાખલ કરો",
            fromAgeLabel: "ઉંમર (થી)",
            toAgeLabel: "ઉંમર (સુધી)",
            fromPlaceholder: "દા.ત. 18",
            toPlaceholder: "દા.ત. 23",
            cancelBtn: "રદ કરો",
            searchBtn: "રેકોર્ડ શોધો",
            pageTitle: "ઉંમર મુજબ શોધ પરિણામો",
            searchAgainBtn: "ફરી શોધ કરો",
            filterPlaceholder: "પ્રદર્શિત પરિણામોને નામથી ફિલ્ટર કરો...",
            loadingText: "ડેટા લોડ થઈ રહ્યો છે...",
            noRecordsText: "કોઈ રેકોર્ડ મળ્યા નથી.",
            fatherHusband: "પિતા/પતિ :",
            address: "સરનામું :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં. :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            check: "ચેક",
            prachar: "પ્રચાર",
            print: "પ્રિન્ટ",
            family: "કુટુંબ",
            addToGroup: "જૂથમાં ઉમેરો" // Added translation
        }
    };

    const txt = t[lang];

    useEffect(() => {
        if (!appId) {
            navigate("/");
        }
    }, [appId, navigate]);

    // --- Form Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (value < 0) return;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleOpenModal = () => {
        setShowModal(true);
    };

    // --- API Fetch Function ---
    const handleSearchSubmit = async (e) => {
        e.preventDefault();

        if (!appId || !formData.from_age || !formData.to_age) {
            alert("Please enter both From Age and To Age.");
            return;
        }

        if (parseInt(formData.from_age) > parseInt(formData.to_age)) {
            alert("'From Age' cannot be greater than 'To Age'.");
            return;
        }

        if ((parseInt(formData.to_age) - parseInt(formData.from_age)) > 5) {
            alert("Please select an age range of maximum 5 years.");
            return;
        }

        setShowModal(false);
        setLoading(true);
        setHasSearched(true);
        setCurrentPage(1);

        try {
            const response = await axios.post(
                `${BASE_URL}/api/age_wise_search`,
                {
                    app_id: appId,
                    from_age: formData.from_age,
                    to_age: formData.to_age
                }
            );

            if (response.data.success) {
                setVoters(response.data.data.result || []);
            } else {
                setVoters([]);
            }
        } catch (error) {
            console.error("Error fetching voters:", error);
            setVoters([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredVoters = voters.filter((voter) => {
        if (!cardSearchTerm) return true;
        const fullName = `${voter.eng_f_name} ${voter.eng_surname}`.toLowerCase();
        return fullName.includes(cardSearchTerm.toLowerCase());
    });

    // --- Pagination Logic ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentVoters = filteredVoters.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredVoters.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    // --- Add to Group API Call ---
    const handleAddToGroup = async (voter) => {
        try {
            const payload = {
                app_id: appId?.toString(),
                user_id: userId?.toString() || appId?.toString(),
                idcard: voter.idcard_no
            };

            const response = await axios.post(`${BASE_URL}/api/ins_my_group`, payload);

            if (response.data.success) {
                alert("Added to group successfully!");
            } else {
                alert("Failed to add to group.");
            }
        } catch (error) {
            console.error("Error adding to group:", error);
            alert("An error occurred while adding to the group.");
        }
    };

    // --- WhatsApp Share Logic ---
    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n\n`;
                    message += `मतदान स्थल :\n`;
                    message += `${member.eng_polling_location}\n\n`;
                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');
            } else {
                alert("Could not fetch family details.");
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
            alert("Error generating message.");
        }
    };

    return (
        <>
            {/* --- SEARCH CRITERIA MODAL --- */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content-box" style={{ maxWidth: '450px' }}>
                        <div className="modal-header p-3">
                            <div>
                                <h2>{txt.modalTitle}</h2>
                                <p className="modal-subtitle">{txt.modalSubtitle}</p>
                            </div>
                            <button className="close-btn-icon" onClick={handleCloseModal}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <form onSubmit={handleSearchSubmit}>
                            <div className="modal-body-grid">
                                <div className="form-group">
                                    <label>{txt.fromAgeLabel}</label>
                                    <input type="number" name="from_age" value={formData.from_age} onChange={handleInputChange} className="form-input" placeholder={txt.fromPlaceholder} min="18" max="120" />
                                </div>
                                <div className="form-group">
                                    <label>{txt.toAgeLabel}</label>
                                    <input type="number" name="to_age" value={formData.to_age} onChange={handleInputChange} className="form-input" placeholder={txt.toPlaceholder} min="18" max="120" />
                                </div>
                            </div>
                            <div className="modal-footer" style={{ marginTop: '20px' }}>
                                <button type="button" className="btn-cancel" onClick={handleCloseModal}>{txt.cancelBtn}</button>
                                <button type="submit" className="btn-search-main" style={{ background: '#2563eb' }}>{txt.searchBtn}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- MAIN PAGE CONTENT --- */}
            <div className={`app-container p-4 ${showModal ? 'blur-background' : ''}`}>

                <Header title={txt.pageTitle} />

                {/* --- PROFESSIONAL ACTION BAR --- */}
                <div className="filter-container">
                    <div className="search-wrapper">
                        <svg className="search-icon-main" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        <input
                            className="search-input-modern"
                            type="text"
                            placeholder={txt.filterPlaceholder}
                            value={cardSearchTerm}
                            onChange={(e) => setCardSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="btn-search-trigger" onClick={handleOpenModal}>
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {txt.searchAgainBtn}
                    </button>
                </div>

                {loading && (
                    <div className="loader-wrapper">
                        <div className="spinner"></div>
                        <p className="loader-text">{txt.loadingText}</p>
                    </div>
                )}

                {!loading && hasSearched && voters.length === 0 && (
                    <div className="text-center text-muted p-5">{txt.noRecordsText}</div>
                )}

                {/* --- RESULTS GRID --- */}
                <div className="row g-4">
                    {!loading && currentVoters.map((voter, index) => (
                        <div className="col-12 col-md-6 col-lg-4" key={voter.id || index}>
                            <div className="voter-card-clean h-100">
                                {/* Name Header */}
                                <div className="voter-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <span className="serial-number">{index + 1 + indexOfFirstItem}.</span>
                                        <span className="voter-name-bold ms-2">{voter.eng_f_name} {voter.eng_surname}</span>
                                    </div>
                                    <button 
                                        onClick={() => handleAddToGroup(voter)}
                                        style={{ 
                                            backgroundColor: '#eff6ff', 
                                            color: '#2563eb', 
                                            border: '1px solid #bfdbfe', 
                                            borderRadius: '20px', 
                                            padding: '4px 10px', 
                                            fontSize: '12px', 
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={(e) => { e.target.style.backgroundColor = '#dbeafe'; }}
                                        onMouseOut={(e) => { e.target.style.backgroundColor = '#eff6ff'; }}
                                    >
                                        + {txt.addToGroup}
                                    </button>
                                </div>
                                <div className="voter-body position-relative">
                                    <div className="map-icon-wrapper">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                    </div>
                                    <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.address}</span><span className="value">{voter.address || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.slnoinpart || voter.serial_no}</span></div>
                                    <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{voter.contact_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{voter.idcard_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>
                                </div>
                                <div className="voter-actions">
                                    <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${voter.contact_no}`} />
                                    <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${voter.contact_no}`} />
                                    <button onClick={() => handleWhatsAppShare(voter)} className="action-btn-wrapper" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                        <span className="btn-label">{txt.slip}</span>
                                    </button>
                                    {/* Updated Print Button with Navigation */}
                                    <ActionButton
                                        icon="🖨"
                                        color="#f97316"
                                        label={txt.print}
                                        onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                    />
                                    
                                    <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* --- PAGINATION --- */}
                {!loading && filteredVoters.length > itemsPerPage && (
                    <div className="pagination-wrapper">
                        <div className="pagination-content">
                            <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <div className="page-numbers">
                                {getPageNumbers().map((number, index) => (
                                    <React.Fragment key={index}>
                                        {number === '...' ? (<span className="page-dots">...</span>) : (
                                            <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                            <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                /* --- PROFESSIONAL FILTER CONTAINER --- */
                .filter-container { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 24px; }
                .search-wrapper { position: relative; flex: 1; max-width: 450px; }
                .search-icon-main { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 18px; height: 18px; }
                .search-input-modern { width: 100%; padding: 12px 16px 12px 42px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; color: #1e293b; outline: none; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .search-input-modern:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
                
                .btn-search-trigger { display: flex; align-items: center; gap: 8px; background-color: #2563eb; color: #ffffff; padding: 12px 20px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: 0.2s; white-space: nowrap; box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2); }
                .btn-search-trigger:hover { background-color: #1d4ed8; transform: translateY(-1px); }

                /* --- LOADER --- */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 40vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                .loader-text { color: #6B7280; font-weight: 500; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                /* --- PAGINATION --- */
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: sticky; bottom: 20px; z-index: 50; pointer-events: none; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 8px 16px; display: flex; align-items: center; gap: 10px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); pointer-events: auto; }
                .pagination-nav-btn { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 50%; background: transparent; border: none; color: #2563EB; cursor: pointer; }
                .pagination-nav-btn:disabled { color: #CBD5E1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 5px; align-items: center; }
                .page-num-btn { width: 32px; height: 32px; border-radius: 50%; border: 1px solid transparent; background: transparent; color: #475569; font-weight: 600; font-size: 14px; cursor: pointer; }
                .page-num-btn.active { background-color: #2563EB; color: white; }

                @media (max-width: 640px) {
                    .filter-container { 
                        flex-direction: row; 
                        align-items: center; 
                        gap: 8px; 
                    }
                    .search-wrapper { 
                        flex: 1; 
                        max-width: none; 
                    }
                    .search-input-modern {
                        padding: 10px 16px 10px 36px;
                        font-size: 13px;
                    }
                    .search-icon-main {
                        left: 10px;
                        width: 16px;
                        height: 16px;
                    }
                    .btn-search-trigger { 
                        width: auto; 
                        padding: 10px 12px;
                        font-size: 13px;
                        gap: 4px;
                    }
                    .btn-search-trigger svg {
                        width: 16px;
                        height: 16px;
                    }
                    
                    /* Adjust grid for mobile */
                    .row.g-4 {
                        margin-left: -8px;
                        margin-right: -8px;
                    }
                    
                    [class*="col-"] {
                        padding-left: 8px;
                        padding-right: 8px;
                    }
                    
                    .modal-footer { 
                        padding: 0; 
                    }
                }
                .blur-background { filter: blur(4px); pointer-events: none; }
            `}</style>
        </>
    );
};
// Updated ActionButton to accept onClick props
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default AgeWiseSearch;